import { useEffect, useState } from 'react'
import { listThreads, renameThread } from '../lib/chatThreads'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'

const CSS = `
.hch{width:250px;flex-shrink:0;border-right:0.5px solid var(--border);background:var(--hull);display:flex;flex-direction:column;min-height:0}
.hch-top{padding:12px 12px 8px;display:flex;flex-direction:column;gap:8px}
.hch-new{display:flex;align-items:center;justify-content:center;gap:6px;font-size:12.5px;font-weight:600;color:var(--text);background:var(--bg);border:1px solid var(--line);border-radius:8px;padding:8px 12px;cursor:pointer;font-family:inherit}
.hch-new:hover{border-color:var(--gold2)}
.hch-q{width:100%;box-sizing:border-box;font-size:12.5px;padding:7px 10px;border-radius:8px;border:1px solid var(--line);background:var(--bg);color:var(--text);font-family:inherit}
.hch-list{flex:1;overflow-y:auto;padding:0 8px 14px}
.hch-g{font-size:10px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:var(--muted);padding:12px 6px 5px}
.hch-it{position:relative;display:block;width:100%;text-align:left;border:none;background:transparent;border-radius:8px;padding:8px 8px;cursor:pointer;font-family:inherit;color:var(--text)}
.hch-it:hover{background:var(--bg)}
.hch-it.on{background:var(--bg);box-shadow:inset 3px 0 0 var(--gold2)}
.hch-t{font-size:12.5px;font-weight:500;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding-right:40px}
.hch-s{font-size:10.5px;color:var(--muted);margin-top:2px;display:flex;gap:6px;align-items:center}
.hch-act{font-size:9.5px;font-weight:700;color:var(--gold2);background:rgba(201,168,76,.14);padding:1px 6px;border-radius:8px}
.hch-tools{position:absolute;right:6px;top:7px;display:none;gap:2px}
.hch-it:hover .hch-tools{display:flex}
.hch-tools span{width:20px;height:20px;border-radius:5px;display:flex;align-items:center;justify-content:center;font-size:12px;color:var(--muted)}
.hch-tools span:hover{background:var(--line);color:var(--text)}
.hch-back{display:none}
@media (max-width:1023px){
  .hch{position:fixed;top:0;bottom:0;left:0;z-index:2500;width:84vw;max-width:320px;box-shadow:0 0 40px rgba(0,0,0,.35);transform:translateX(-105%);transition:transform .2s ease}
  .hch.open{transform:none}
  .hch-back.open{display:block;position:fixed;inset:0;z-index:2400;background:rgba(10,22,40,.45)}
}
`
function groupOf(ts) {
  const d = new Date(ts); const now = new Date()
  const day = x => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  const diff = Math.round((day(now) - day(d)) / 86400000)
  if (diff <= 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  if (diff < 7) return 'Previous 7 days'
  if (diff < 30) return 'Previous 30 days'
  return 'Older'
}
const ORDER = ['Today', 'Yesterday', 'Previous 7 days', 'Previous 30 days', 'Older']
const when = ts => {
  const d = new Date(ts)
  return groupOf(ts) === 'Today' || groupOf(ts) === 'Yesterday'
    ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export default function ChatHistory({ clientId, currentId, onOpen, onNew, tick = 0, open, onClose, onDeletedCurrent, saveErr = '' }) {
  const [rows, setRows] = useState(null)
  const [q, setQ] = useState('')
  const [err, setErr] = useState('')

  async function load() {
    if (!clientId) return
    try { setRows(await listThreads(clientId)); setErr('') } catch (e) { setErr(e.message); setRows([]) }
  }
  useEffect(() => { load() }, [clientId, tick]) // eslint-disable-line

  async function rename(t, e) {
    e.stopPropagation()
    const v = window.prompt('Rename chat', t.title || '')
    if (!v || !v.trim()) return
    try { await renameThread(t.id, v.trim()); load() } catch (x) { alert('Could not rename: ' + x.message) }
  }
  async function remove(t, e) {
    e.stopPropagation()
    if (!window.confirm('Delete the chat "' + (t.title || 'chat') + '" from your history? You can undo it for 10 seconds.')) return
    try { await deleteWithUndo(supabase, { table: 'ai_threads', id: t.id, label: 'chat "' + (t.title || 'chat') + '"', onUndone: () => load() }); if (t.id === currentId && onDeletedCurrent) onDeletedCurrent(); load() } catch (x) { alert('Could not delete: ' + x.message) }
  }

  const list = (rows || []).filter(t => !q.trim() || (t.title || '').toLowerCase().includes(q.trim().toLowerCase()))
  const groups = ORDER.map(g => [g, list.filter(t => groupOf(t.updated_at) === g)]).filter(([, l]) => l.length)

  return (
    <>
      <style>{CSS}</style>
      <div className={'hch-back' + (open ? ' open' : '')} onClick={onClose} />
      <div className={'hch' + (open ? ' open' : '')}>
        <div className="hch-top">
          <button className="hch-new" onClick={onNew}><span style={{ fontSize: 15, lineHeight: 1 }}>+</span> New chat</button>
          <input className="hch-q" placeholder={'Search chats\u2026'} value={q} onChange={e => setQ(e.target.value)} />
        </div>
        {saveErr && <div style={{ fontSize: 11, color: '#c0434a', padding: '0 14px 6px' }}>Not saved: {saveErr}</div>}
        <div className="hch-list">
          {rows == null ? <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 6px' }}>Loading&hellip;</div>
            : err ? <div style={{ fontSize: 11.5, color: '#c0434a', padding: '10px 6px' }}>History unavailable: {err}</div>
            : groups.length === 0 ? <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 6px' }}>{q ? 'No matching chats.' : 'Your chats will appear here.'}</div>
            : groups.map(([g, l]) => (
              <div key={g}>
                <div className="hch-g">{g}</div>
                {l.map(t => {
                  const acts = (t.action_refs || []).filter(r => r !== 'action')
                  const n = (t.action_refs || []).length
                  return (
                    <div key={t.id} role="button" tabIndex={0} className={'hch-it' + (t.id === currentId ? ' on' : '')} onClick={() => onOpen(t)} onKeyDown={e => { if (e.key === 'Enter') onOpen(t) }}>
                      <div className="hch-t" title={t.title}>{t.title || 'New chat'}</div>
                      <div className="hch-s">
                        <span>{when(t.updated_at)}</span>
                        {n > 0 && <span className="hch-act" title={acts.join(', ') || 'Data changed'}>{acts.length ? acts.slice(0, 2).join(' \u00b7 ') + (acts.length > 2 ? ' +' + (acts.length - 2) : '') : n + ' action' + (n > 1 ? 's' : '')}</span>}
                      </div>
                      <div className="hch-tools">
                        <span title="Rename" onClick={e => rename(t, e)}>&#9998;</span>
                        <span title="Delete" onClick={e => remove(t, e)}>&times;</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
        </div>
      </div>
    </>
  )
}
